import { MediumParams } from 'src/modules/user-input/components/MediumParams.tsx';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { MediumParamsChangeEventDetails } from 'src/modules/user-input/MediumParams/types.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalTopSection,
} from 'src/modules/common/components/CalculatorModalTopSection/CalculatorModalTopSection';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { Units } from 'uom-units';
import { reducer } from 'src/modules/user-input/calcFtHead/reducer.ts';
import { initialState } from 'src/modules/user-input/calcFtHead/initialState.ts';
import { calcPressureFromHeight } from 'src/modules/user-input/calcFtHead/calcPressureFromHeight.ts';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { Modal } from 'src/modules/common/components/Modal/Modal.tsx';
import { ModalHeader } from 'src/modules/common/components/Modal/ModalHeader.tsx';
import { ModalBody } from 'src/modules/common/components/Modal/ModalBody.tsx';
import { ModalFooter } from 'src/modules/common/components/Modal/ModalFooter.tsx';
import { Button } from 'src/common/Button/Button';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector';
import { ConfigContext } from 'src/configContext';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { ModalProps } from 'src/modules/user-input/types';

type Props = ModalProps & {
    uuid: string;
    fluid: string[];
}

export const CalcFtHead = ({
    opened,
    title,
    titleResult,
    description,
    allowedUnits,
    targetUnit,
    uuid,
    fluid,
    handleModalClose,
    handleModalTransfer,
}: Props) => {
    const { userSettings: { currentLocale, currentRegion } } = useContext(ConfigContext);

    const mediumParamsRef = useRef({ reset: () => null });

    const [value, setValue] = useState('');
    const [unit, setUnit] = useState(targetUnit);

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
    });

    const onMediumParamsChange = useCallback((mediumParams: MediumParamsChangeEventDetails) => {
        dispatch({
            type: 'mediumParams_change',
            payload: mediumParams,
        });
    }, [dispatch]);

    const maxDiffPressure = useMemo(() => {
        try {
            return convert(
                calcPressureFromHeight(
                    convert(state.ftHead, Units.Foot, Units.Meter),
                    convert(state.density, Units.PoundPerCubicFoot, Units.KilogramPerCubicMeter),
                ),
                Units.Pascal,
                unit,
            );
        } catch (e) {
            return '';
        }
    }, [state, unit]);

    const handleReset = useCallback(() => {
        dispatch({ type: 'reset' });
        mediumParamsRef.current.reset();
    }, []);

    useEffect(() => {
        setValue(maxDiffPressure);
    }, [maxDiffPressure]);

    useEffect(() => {
        setUnit(targetUnit);
    }, [targetUnit]);

    return (
        <Modal
            opened={opened}
            handleClose={handleModalClose}
        >
            <ModalHeader
                handleClose={handleModalClose}
                handleReset={handleReset}
                title={title}
            >
                <I18n messageId={description}/>
            </ModalHeader>

            <ModalBody>
                <CalculatorModalLayout>
                    <CalculatorModalTopSection>
                        <MediumParams
                            uuid={uuid}
                            fluid={fluid}
                            currentRegion={currentRegion}
                            allowedUnits={allowedUnits}
                            currentLocale={currentLocale}
                            onChange={onMediumParamsChange}
                            showHeatCapacity={false}
                            mediumParamsRef={mediumParamsRef}
                        />
                    </CalculatorModalTopSection>
                    <CalculatorModalMainSection
                        title={titleResult}
                        result={numberFormatter(maxDiffPressure, currentLocale)}
                        selectedUnit={unit}
                        onUnitChange={setUnit}
                        allowedUnits={allowedUnits.dpmv}
                    >
                        <DecimalField
                            name="ftHead"
                            inputSize="md"
                            labelText={MESSAGES.FT_HEAD}
                            value={state.ftHead}
                            endAdornment={(
                                <UnitSelector
                                    values={[Units.Foot]}
                                    selected={Units.Foot.name}
                                    onChange={() => null}
                                />
                            )}
                            currentLocale={currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'ftHead_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                    </CalculatorModalMainSection>
                </CalculatorModalLayout>
            </ModalBody>
            <ModalFooter>
                <Button
                    kind="secondary"
                    fullWidth={true}
                    onClick={handleModalClose}
                >
                    <I18n messageId={MESSAGES.CANCEL}/>
                </Button>

                <Button
                    fullWidth={true}
                    onClick={() => {
                        handleModalTransfer({
                            value,
                            unit,
                        });
                    }}
                >
                    <I18n messageId={MESSAGES.apply}/>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
